import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { sessionsApi, reportsApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { Send, Bomb, DollarSign, Lock, Clock, Users, Loader2, X, Flag } from "lucide-react";

const TIPS = [3, 10, 25, 100];

export default function SessionRoom() {
  const { sid } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const tipped = params.get("tipped") === "1";
  const { user } = useAuth();
  const nav = useNavigate();
  const [sess, setSess] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [showTip, setShowTip] = useState(false);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user) { nav("/auth?mode=login"); return; }
    sessionsApi.get(sid, token).then(setSess).catch((e) => {
      const code = e?.response?.status;
      if (code === 410) toast.error("This session has self-destructed");
      else if (code === 403) toast.error("Invite required");
      else toast.error("Couldn't open the room");
      nav("/sessions");
    });
  }, [sid, token, user, nav]);

  useEffect(() => {
    if (!sess) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await sessionsApi.messages(sid);
        if (cancelled) return;
        if (r.expired) { toast.warning("Room detonated"); nav("/sessions"); return; }
        setMsgs(r.messages);
        setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 0);
      } catch (e) { /* ignore */ }
    };
    tick();
    const i = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(i); };
  }, [sess, sid, nav]);

  useEffect(() => { if (tipped) toast.success("🌿 Tip received — thank the host"); }, [tipped]);

  if (!sess) return <div className="min-h-screen"><NavBar /><div className="text-center py-20 opacity-60"><Loader2 className="mx-auto animate-spin" /></div></div>;

  const isHost = user?.id === sess.host_user_id;
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await sessionsApi.send(sid, text);
      setText("");
      // refresh messages
      const r = await sessionsApi.messages(sid);
      if (!r.expired) setMsgs(r.messages);
    } catch (e) {
      toast.error(e?.response?.status === 410 ? "Session detonated" : "Send failed");
    } finally { setBusy(false); }
  };

  const detonate = async () => {
    if (!confirm("Detonate this room? All messages will be permanently destroyed.")) return;
    try {
      await sessionsApi.close(sid);
      toast.success("💥 Room detonated");
      nav("/sessions");
    } catch (e) { toast.error("Couldn't detonate"); }
  };

  const tip = async (amount) => {
    try {
      const r = await sessionsApi.tip(sid, amount);
      if (r?.url) window.location.href = r.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Tip failed"); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <NavBar />
      <Toaster position="top-right" theme="light" richColors />
      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-8 relative">
        {/* header */}
        <div className="card-x card-magenta bg-[#FAF6E8] p-5 mb-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-[#FF1A8C] border-2 border-[#0B0F0C] flex items-center justify-center text-white font-extrabold flex-shrink-0">{(sess.host_handle || "?")[0]?.toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-2xl font-extrabold leading-none truncate">{sess.title}</div>
              <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest opacity-65 truncate">@{sess.host_handle} · hosting · {sess.topic}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-[10px] font-mono-x font-bold uppercase tracking-widest">
            <span className="px-2 py-1 rounded-full border-2 border-[#0B0F0C] flex items-center gap-1"><Lock size={10} /> Encrypted</span>
            <span className="px-2 py-1 rounded-full border-2 border-[#0B0F0C] flex items-center gap-1"><Clock size={10} /> {sess.ttl_minutes}m TTL</span>
            <span className="px-2 py-1 rounded-full border-2 border-[#0B0F0C] flex items-center gap-1"><Users size={10} /> {(sess.members || []).length}</span>
            {sess.tips_total > 0 && <span className="sticker sticker-lemon !text-[10px]">${Math.round(sess.tips_total)} tipped</span>}
            {user && user.id !== sess.host_user_id && (
              <button
                data-testid="session-flag-btn"
                onClick={async () => {
                  const reason = window.prompt("Why are you reporting this session? (admins will review)");
                  if (reason === null) return;
                  try { await reportsApi.create("session", sid, reason); toast.success("Reported. Admin notified."); }
                  catch { toast.error("Couldn't send report"); }
                }}
                title="Report this session"
                className="px-2 py-1 rounded-full border-2 border-[#0B0F0C] flex items-center gap-1 hover:bg-[#FFE74C]"
              ><Flag size={10} /> Report</button>
            )}
          </div>
        </div>

        {/* chat */}
        <div ref={scrollRef} className="card-x card-cyan bg-[#FAF6E8] p-4 h-[55vh] overflow-y-auto mb-3">
          {msgs.length === 0 ? (
            <div className="h-full flex items-center justify-center text-center opacity-50 px-6">
              <div>
                <div className="text-5xl mb-2">🚪</div>
                <div className="font-mono-x font-bold uppercase tracking-widest text-xs">Room's open. Drop a message.</div>
                <div className="text-[10px] mt-1 opacity-60">Each message encrypts on its way to the server. Detonates on close.</div>
              </div>
            </div>
          ) : msgs.map((m) => (
            <div key={m.id} className={`mb-3 flex ${m.user_id === user.id ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl border-2 border-[#0B0F0C] ${m.user_id === user.id ? "bg-[#15FF73] rounded-br-sm" : "bg-white rounded-bl-sm"}`}>
                <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest opacity-60 mb-0.5">@{m.user_handle}</div>
                <div className="text-sm leading-relaxed whitespace-pre-line">{m.body}</div>
              </div>
            </div>
          ))}
        </div>

        {/* compose */}
        <div className="flex items-center gap-2 mb-3">
          <input
            data-testid="session-input"
            value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Message…"
            className="flex-1 bg-[#FAF6E8] border-2 border-[#0B0F0C] rounded-full px-5 h-12 text-sm font-medium outline-none focus:shadow-[3px_3px_0_#0B0F0C]"
          />
          <button data-testid="session-send" onClick={send} disabled={busy || !text.trim()} className="btn-x btn-primary !py-2.5 !px-4 disabled:opacity-50">
            <Send size={13} strokeWidth={2.4} />
          </button>
        </div>

        {/* actions */}
        <div className="flex flex-wrap gap-2">
          {!isHost && (
            <button data-testid="session-tip-open" onClick={() => setShowTip(true)} className="btn-x btn-ghost !py-2 !px-3 !text-xs">
              <DollarSign size={12} strokeWidth={2.6} /> Tip the host
            </button>
          )}
          {isHost && (
            <button data-testid="session-detonate" onClick={detonate} className="btn-x btn-primary !bg-[#FF1A8C] !py-2 !px-3 !text-xs">
              <Bomb size={12} strokeWidth={2.6} /> Detonate room
            </button>
          )}
          <Link to="/sessions" className="btn-x btn-ghost !py-2 !px-3 !text-xs">← All sessions</Link>
        </div>

        {showTip && (
          <div className="fixed inset-0 z-[100] bg-[#0B0F0C]/85 flex items-center justify-center p-4">
            <div className="card-x card-magenta bg-[#FAF6E8] p-5 max-w-sm w-full">
              <div className="flex items-center mb-3">
                <span className="font-display text-2xl font-extrabold">Tip the host</span>
                <button onClick={() => setShowTip(false)} className="ml-auto w-9 h-9 rounded-full border-2 border-[#0B0F0C] flex items-center justify-center"><X size={13} strokeWidth={2.4} /></button>
              </div>
              <p className="text-xs opacity-70 mb-4">Stripe-secured. Funds settle directly to <span className="font-bold">@{sess.host_handle}</span>'s wallet. No questions asked.</p>
              <div className="grid grid-cols-2 gap-2">
                {TIPS.map((a) => (
                  <button key={a} data-testid={`session-tip-${a}`} onClick={() => tip(a)} className="btn-x btn-primary justify-center">
                    ${a}
                  </button>
                ))}
              </div>
              <p className="text-[10px] font-mono-x font-bold uppercase tracking-widest mt-4 opacity-50 text-center">Stripe test mode</p>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
